import { useEffect } from 'react';
import { ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { AnyRow, isGhostRow } from './types';

const NATIVE_CHECKBOX_SELECTOR = '.ag-selection-checkbox input';

export type SelectionCellRendererParams = {
  getRowLabel: (row: unknown) => string | undefined;
};

export type SelectionColumnHeaderParams = {
  /**
   * Visually hidden accessible name for the selection column header, or
   * `undefined` when AG Grid's select-all checkbox is present and already
   * names the column.
   */
  label: string | undefined;
};

type SelectionCellRendererProps = ICellRendererParams<
  AnyRow<unknown, never, string>
> &
  SelectionCellRendererParams;

/**
 * Points AG Grid's row checkbox at a per-row accessible name.
 *
 * AG Grid names every row checkbox from the static `ariaRowToggleSelection`
 * locale key — identical for every row, so a screen reader user cannot tell
 * what they are selecting. There is no per-node hook: the translate function
 * never receives the row.
 *
 * We therefore render a hidden label and point the native input at it with
 * `aria-labelledby`, which wins over `aria-label` in accessible-name
 * computation.
 */
export const SelectionCellRenderer = (props: SelectionCellRendererProps) => {
  const { api, node, data, eGridCell, getRowLabel } = props;

  // Group rows have no data, and ghost rows are hidden placeholders carrying
  // only an id. Neither has a label, so we're using AG Grid default.
  const label =
    data && !isGhostRow(data) ? getRowLabel?.(data.data) : undefined;
  const labelId = label
    ? `ardoq-row-label-${api.getGridId()}-${node.id}`
    : null;

  // The native checkbox is appended by a ref callback on the ancestor
  // `.ag-cell-wrapper`, and only once `includeSelection` flips in a later render
  // pass. React runs descendant effects before ancestor refs, so a one-shot
  // query here would find nothing — hence the observer below.
  useEffect(() => {
    const cell = eGridCell;
    if (!cell) {
      return;
    }

    const applyLabel = () => {
      const input = cell.querySelector(NATIVE_CHECKBOX_SELECTOR);
      if (!input) {
        return;
      }
      if (!labelId) {
        // The row lost its label (e.g. it became a ghost row).
        input.removeAttribute('aria-labelledby');
      } else if (input.getAttribute('aria-labelledby') !== labelId) {
        input.setAttribute('aria-labelledby', labelId);
      }
    };

    applyLabel();
    // The checkbox is appended (and recreated) as a child, hence childList.
    // And AG Grid's AgCheckbox.refreshAriaLabelledBy strips aria-labelledby
    // whenever it rewrites aria-label — which it does on  every rowSelected,
    // dataChanged or selectableChanged — hence attributes.
    const observer = new MutationObserver(applyLabel);
    observer.observe(cell, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-labelledby'],
    });
    // Deliberately no attribute cleanup here. AG Grid recreates the cell
    // component on data changes, and the outgoing instance's teardown runs
    // after the incoming one has already applied its label — removing the
    // attribute here would wipe the fresh one.
    return () => observer.disconnect();
  }, [eGridCell, labelId]);

  if (!labelId) {
    return null;
  }

  // `aria-hidden` keeps the text out of browse mode, so the gridcell is not read
  // as the row name on top of the checkbox.
  return (
    <VisuallyHidden elementType="span" id={labelId} aria-hidden="true">
      {label}
    </VisuallyHidden>
  );
};

/**
 * Names the selection column header when nothing else does.
 *
 * AG Grid renders the user header component as a sibling of its select-all
 * checkbox, so when that checkbox is shown its aria-label already names the
 * column and ours would only be appended to it. We therefore render a label
 * solely for the `hideHeaderCheckbox` case, where the columnheader would
 * otherwise be completely anonymous.
 */
export const SelectionColumnHeader = (
  props: IHeaderParams<AnyRow<unknown, never, string>> &
    SelectionColumnHeaderParams
) =>
  props.label ? (
    <VisuallyHidden elementType="span">{props.label}</VisuallyHidden>
  ) : null;
